import { inject, Injectable, OnDestroy } from '@angular/core';
import { EMPTY, Observable, of, Subscription } from 'rxjs';
import { StructureList } from '../models/structure-list';
import { WebdokuService } from '../../web-doku/webdoku.service';
import { LDWDocument } from '../models/LDWDocument';
import { HttpClient, HttpHeaders, HttpStatusCode } from '@angular/common/http';
import { AppUrls } from '../../route/app-urls';
import { catchError, map } from 'rxjs/operators';
import { StructuredInfoListDto } from '../dto/structured-info-list-dto';
import { StructureInfoFactory } from '../helpers/structure-info.factory';
import { DocumentChanges } from '../models/document-changes';
import { DocumentChangesDto } from '../dto/document-changes-dto';
import { DocumentChangesFactory } from '../helpers/document-changes.factory';
import { DocumentHistoryFactory } from '../helpers/document-history.factory';
import { DocumentHistory } from '../models/document-history';
import { ActionResult } from '../models/action-result';
import { SetStatusDto } from '../dto/set-status-dto';
import { TranslateService } from '@ngx-translate/core';
import { DocumentRequestDto } from '../dto/document-request-dto';
import { DocumentHistoryRequestDto } from '../dto/document-history-request-dto';
import { DocumentHistoryDto } from '../dto/document-history-dto';
import { DocumentDto } from '../dto/document-dto';
import { DocumentDetail } from '../models/document-detail';
import { ErrorHandlers } from '../helpers/error-handlers';
import { CopyFieldFactory } from '../helpers/copy-field.factory';
import { CopyFieldDto } from '../dto/copy-field-dto';
import { DocumentCopyDataGroup } from '../models/document-copydata-group';
import { CopyDocumentDto } from '../dto/copy-document-dto';
import { WebDokuTreeNodeFactory } from '../helpers/webdoku-tree-node.factory';
import { WebDokuTreeNodeDto } from '../dto/webdoku-tree-node-dto';
import { injectQuery } from '@ngneat/query';

@Injectable({
  providedIn: 'root',
})
export class DocumentService implements OnDestroy {
  private webDokuService = inject(WebdokuService);

  protected wdSubscription: Subscription;
  docCache: Map<string, DocumentDetail> = new Map<string, DocumentDetail>();
  private useQuery = injectQuery();
  private translate = inject(TranslateService);
  private http = inject(HttpClient);

  constructor() {
    const webDokuService = this.webDokuService;

    this.wdSubscription = webDokuService.webDokuChange.subscribe(() => {
      this.webDokuChanged();
    });
  }

  private static updateCacheBaseData(doc: DocumentDetail | undefined, valueItem: LDWDocument) {
    if (doc == undefined) {
      return;
    }
    doc.name = valueItem.name;
    doc.images = valueItem.images;
    doc.text = valueItem.text;
    doc.status = valueItem.status;
    doc.treePath = valueItem.treePath;
    doc.hasHistory = valueItem.hasHistory;
  }

  // wird nicht aufgerufen !
  ngOnDestroy(): void {
    this.wdSubscription.unsubscribe();
  }

  public getDocumentNavigationData(nodeId: string): Observable<StructureList> {
    return this.getNavigationData(AppUrls.urlDocumentNavigation, nodeId);
  }

  public getBasicNavigationData(nodeId: string): Observable<StructureList> {
    return this.getNavigationData(AppUrls.urlBasicNavigation, nodeId);
  }

  public getCustomNavigationData(nodeId: string): Observable<StructureList> {
    return this.getNavigationData(AppUrls.urlCustomNavigation, nodeId);
  }

  private getNavigationData(url: string, nodeId: string): Observable<StructureList> {
    return this.http
      .get<StructuredInfoListDto>(AppUrls.getApiUrl(url, 'get') + this.formatParams('nodeId', nodeId))
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => {
          const converted = StructureInfoFactory.fromStructureDTOList(dtoRaw, this.webDokuService.currentWebDokuId);
          this.updateDocCacheFromList(converted);
          return converted;
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public getDocumentTree() {
    return this.getNavigationTree(AppUrls.urlDocumentNavigation);
  }

  public getBasicTree() {
    return this.getNavigationTree(AppUrls.urlBasicNavigation);
  }

  public getCustomNavTree() {
    return this.getNavigationTree(AppUrls.urlCustomNavigation);
  }

  private getNavigationTree(url: string) {
    return this.useQuery(
      {
        queryKey: [url] as const,
        queryFn: () => this.http.get<WebDokuTreeNodeDto[]>(AppUrls.getApiUrl(url, 'getTree') + this.formatParams('', '')).pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoList) => {
          const converted = WebDokuTreeNodeFactory.fromDTO(dtoList);
          return converted;
        }),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        }))
      }
    )
  }

  private formatParams(name: string, value: string): string {
    let param = '?webDokuId=' + this.webDokuService.currentWebDokuId;
    if (name != null && name.length > 0 && value != null && value.length > 0) {
      // encodeURIComponent is needed to handle special characters like # for projectids in the tree
      param = param + '&' + name + '=' + encodeURIComponent(value);
    }
    return param;
  }

  public getDocument(docId: string): Observable<DocumentDetail> {
    if (this.docCache.has(docId)) {
      // @ts-ignore
      return of(this.docCache.get(docId));
    }

    return this.http
      .get<DocumentDto>(AppUrls.getApiUrl(AppUrls.urlDocument, 'get') + this.formatParams('documentId', docId))
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => {
          const converted = StructureInfoFactory.fromDocumentDTO(this.translate, dtoRaw);
          this.addOrUpdateCacheItem(converted);
          return converted;
        }),
        catchError((error) => {
          if (error.status === HttpStatusCode.NoContent) {
            return EMPTY;
          }
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public getDocumentChanges(docId: string): Observable<DocumentChanges[]> {
    return this.http
      .get<DocumentChangesDto[]>(
        AppUrls.getApiUrl(AppUrls.urlDocument, 'getchanges') + this.formatParams('documentId', docId)
      )
      .pipe(
        // retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => DocumentChangesFactory.fromDTO(dtoRaw)),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public getDocumentHistory(docId: string): Observable<DocumentHistory[]> {
    return this.http
      .get<DocumentHistoryDto[]>(
        AppUrls.getApiUrl(AppUrls.urlDocument, 'gethistory') + this.formatParams('documentId', docId)
      )
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => DocumentHistoryFactory.fromDTO(dtoRaw)),
        catchError((error) => {
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public getCopyData(docId: string): Observable<DocumentCopyDataGroup[]> {
    return this.http
      .get<CopyFieldDto[]>(
        AppUrls.getApiUrl(AppUrls.urlDocument, 'getcopydata') + this.formatParams('documentId', docId)
      )
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map((dtoRaw) => CopyFieldFactory.fromDTO(dtoRaw)),
        catchError((error) => {
          console.log(error);
          return ErrorHandlers.errorHandler(error);
        })
      );
  }

  public copyDocument(data: CopyDocumentDto): Observable<ActionResult> {
    data.webDokuId = this.webDokuService.currentWebDokuId;
    return this.http
      .post<ActionResult>(AppUrls.getApiUrl(AppUrls.urlDocument, 'copyDocument'), JSON.stringify(data))
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map(() => {
          const result = new ActionResult();
          result.success = true;
          return result;
        }),
        catchError((error) => {
          const result = new ActionResult();
          result.success = false;
          result.message = error?.error?.message ?? this.translate.instant('DOCUMENT.DLG_COPYFAILED');
          return of(result);
        })
      );
  }

  public updateStatus(docId: string, statusId: string): Observable<ActionResult> {
    if (this.docCache.has(docId)) {
      // @ts-ignore
      this.docCache.delete(docId);
    }

    const setDto = new SetStatusDto();
    setDto.webDokuId = this.webDokuService.currentWebDokuId;
    setDto.documentId = docId;
    setDto.statusId = statusId;
    return this.http
      .post<ActionResult>(AppUrls.getApiUrl(AppUrls.urlDocument, 'setStatus'), JSON.stringify(setDto))
      .pipe(
        //retry(this.maxRetries),   //ToDo: use retryWhen to avoid multiple calls on special errors, add delay
        map(() => {
          const result = new ActionResult();
          result.success = true;
          return result;
        }),
        catchError(() => {
          const result = new ActionResult();
          result.success = false;
          result.message = this.translate.instant('STATUS.SETSTATUSFAILED');
          return of(result);
        })
      );
  }

  public getPdf(docId: string): Observable<Blob> {
    const docDto = new DocumentRequestDto();
    docDto.webDokuId = this.webDokuService.currentWebDokuId;
    docDto.documentId = docId;
    return this.http.post(AppUrls.getApiUrl(AppUrls.urlDocument, 'getdocument'), JSON.stringify(docDto), {
      responseType: 'blob',
    });
  }

  public savePdf(docId?: string, blob?: Blob) {
    if (!docId || !blob) {
      return of(new Error('no docID or Blob found'));
    }
    const formData = new FormData();
    formData.append('blob', blob, 'test.pdf');

    return this.http.post<any>(AppUrls.getApiUrl(AppUrls.urlDocument, 'savedocument'), formData, {
      headers: new HttpHeaders()
        .set('Content-Type', 'form-data')
        .set('WebDoku-Id', this.webDokuService.currentWebDokuId)
        .set('Document-Id', docId),
    });
  }

  public saveFdf(docId?: string, blob?: Blob) {
    if (!docId || !blob) {
      return of(new Error('no docID or Blob found'));
    }
    const formData = new FormData();
    formData.append('blob', blob, 'test.fdf');

    return this.http.post<any>(AppUrls.getApiUrl(AppUrls.urlDocument, 'savedocumentfdf'), formData, {
      headers: new HttpHeaders()
        .set('Content-Type', 'form-data')
        .set('WebDoku-Id', this.webDokuService.currentWebDokuId)
        .set('Document-Id', docId),
    });
  }

  public getHistoryPdf(docId: string, archiveFileName: string): Observable<Blob> {
    const docDto = new DocumentHistoryRequestDto();
    docDto.webDokuId = this.webDokuService.currentWebDokuId;
    docDto.documentId = docId;
    docDto.archiveFile = archiveFileName;
    return this.http.post(AppUrls.getApiUrl(AppUrls.urlDocument, 'gethistorydocument'), JSON.stringify(docDto), {
      responseType: 'blob',
    });
  }

  private webDokuChanged() {
    this.docCache.clear();
  }

  /**
   * updates doccache with up-to-date data from list
   * this does not add document to cache
   * @param info
   * @private
   */
  private updateDocCacheFromList(info: StructureList) {
    for (let i = 0; i < info.data.length; i++) {
      const item = info.data[i];
      if (item.isDocument()) {
        if (this.docCache.has(item.id)) {
          // if the document is already in the cache, it will be updated with the new data, as they are more recent
          const doc = this.docCache.get(item.id);
          DocumentService.updateCacheBaseData(doc, item);
        }
      }
    }
  }

  /**
   * adds (or updates) a document to document cache
   * @param valueItem document data
   * @private
   */
  private addOrUpdateCacheItem(valueItem: DocumentDetail) {
    let doc: DocumentDetail | undefined = undefined;
    if (this.docCache.has(valueItem.id)) {
      doc = this.docCache.get(valueItem.id);
    }
    if (doc == undefined) {
      doc = new DocumentDetail();
      doc.id = valueItem.id;
      this.docCache.set(valueItem.id, doc);
    }

    DocumentService.updateCacheBaseData(doc, valueItem);
    doc.additionalInfos = valueItem.additionalInfos;
    doc.created = valueItem.created;
    doc.createdBy = valueItem.createdBy;
    doc.job = valueItem.job;
  }

  public deleteDocument(docId: string): Observable<boolean> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      body: {
        webDokuId: this.webDokuService.currentWebDokuId,
        documentId: docId,
      },
    };
    return this.http.request<boolean>('delete', AppUrls.getApiUrl(AppUrls.urlDocument, 'delete'), options).pipe(
      map(() => {
        return true;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  public changedDocument(docId: string): Observable<boolean> {
    const docDto = new DocumentRequestDto();
    docDto.webDokuId = this.webDokuService.currentWebDokuId;
    docDto.documentId = docId;

    return this.http.post<boolean>(AppUrls.getApiUrl(AppUrls.urlDocument, 'changed'), JSON.stringify(docDto)).pipe(
      map(() => {
        return true;
      }),
      catchError(() => {
        return of(false);
      })
    );
  }
}
